import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import Endpoints from 'constants/endpoints';
import { HTTP_STATS_CODE } from 'constants/general';
import {
  CLASS_SESSION_STATUS,
  SESSION_TASKS_AMEND_ERROR,
  UNLOCK_REQUEST_STATUS
} from 'enums';
import {
  AddClassMembersPayload,
  AmendAllowlistPayload,
  AmendSessionTasksPayload,
  ClassAttendanceResponse,
  ClassConcernCriteria,
  ClassCandidateMember,
  ClassFocusSession,
  ClassSessionListItem,
  CreateClassPayload,
  FetchCandidateMembersArgs,
  ReleaseStudentPayload,
  RemoveClassMemberPayload,
  RenameClassPayload,
  ResolveUnlockRequestPayload,
  StartClassSessionPayload,
  UpdateConcernCriteriaPayload
} from 'interfaces/classes.interface';
import { TeamsMembers } from 'interfaces/team.interface';
import { toast } from 'react-toastify';
import focusBearApi from 'services/axios-config';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import type { RootState } from 'store';
import { getMaxBy } from 'utils/array.util';
import { toastApiRequestError } from 'utils/api-error.util';
import {
  beginCandidateListFetch,
  beginCandidateSearchFetch,
  buildClassRosterPartitions,
  dedupeClassRoster,
  invalidateCandidateMemberFetches,
  isCurrentCandidateListFetch,
  isCurrentCandidateSearchFetch
} from 'utils/class-management.util';
import {
  updateAreAttendanceLoading,
  setConcernCriteria,
  updateAreCandidateMembersLoading,
  updateAreCandidateMembersRestricted,
  updateAreClassesLoading,
  updateAreClassMembersLoading,
  updateAreParticipantsLoading,
  updateAreSessionsLoading,
  updateAreSharedFocusModesLoading,
  updateAreUnlockRequestsLoading,
  updateAttendance,
  updateCandidateMembers,
  updateClassMembers,
  updateClasses,
  updateClassesInitialized,
  updateIsAddingMembers,
  updateIsAmendingAllowlist,
  updateIsAmendingSessionTasks,
  updateIsArchivingClass,
  updateIsCreatingClass,
  updateIsEndingSession,
  updateIsRenamingClass,
  updateIsLiveSessionLoading,
  updateIsStartingSession,
  updateLiveSession,
  updateParticipants,
  updateReportForSession,
  updateReportLoadingForSession,
  updateAllowlistTemplateSavedSessionId,
  updateIsSavingAllowlistToTemplate,
  updateReleasingStudentId,
  updateRemovingMemberId,
  updateResolvingUnlockRequestId,
  updateResolvingUnlockStudentId,
  updateSelectedClass,
  updateSessions,
  updateSharedFocusModes,
  updateShowStartSessionModal,
  updateUnlockRequests
} from './slice';

export const fetchTeacherClasses = createAsyncThunk(
  'class_management/fetch_teacher_classes',
  async (_, { dispatch }) => {
    try {
      dispatch(updateAreClassesLoading(true));
      const { data } = await focusBearApi.get(
        Endpoints.CLASS_MANAGEMENT.CLASSES
      );
      const classes = Array.isArray(data) ? data : [];
      dispatch(updateClasses(classes));
      return classes;
    } catch (error) {
      captureException(error);
      throw error;
    } finally {
      dispatch(updateAreClassesLoading(false));
      dispatch(updateClassesInitialized(true));
    }
  }
);

/**
 * Team-shared focus modes visible to the caller (their teams + those teams'
 * ancestor chain) — plan A7 "list-shared-for-me". Fails silently: the
 * StartSessionModal inline allow-list editor works without it (endpoint is
 * pending backend finalisation).
 */
export const fetchSharedFocusModes = createAsyncThunk(
  'class_management/fetch_shared_focus_modes',
  async (_, { dispatch }) => {
    try {
      dispatch(updateAreSharedFocusModesLoading(true));
      const { data } = await focusBearApi.get(Endpoints.FOCUS_MODE_SHARED);
      dispatch(updateSharedFocusModes(Array.isArray(data) ? data : []));
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(updateAreSharedFocusModesLoading(false));
    }
  }
);

/**
 * A7 save-back: persist an (ended) session's final allow-list onto the
 * team-shared focus mode it was started from.
 */
export const saveAllowlistToTemplate = createAsyncThunk(
  'class_management/save_allowlist_to_template',
  async (sessionId: string, { dispatch }) => {
    try {
      dispatch(updateIsSavingAllowlistToTemplate(true));
      const { status } = await focusBearApi.post(
        Endpoints.CLASS_MANAGEMENT.SAVE_ALLOWLIST_TO_TEMPLATE(sessionId)
      );
      if ([HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)) {
        toast.success(t('class_mode.allowlist_saved_to_template'));
        dispatch(updateAllowlistTemplateSavedSessionId(sessionId));
        dispatch(fetchSharedFocusModes());
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      toastApiRequestError(error);
      captureException(error);
    } finally {
      dispatch(updateIsSavingAllowlistToTemplate(false));
    }
  }
);

export const createClass = createAsyncThunk(
  'class_management/create_class',
  async (payload: CreateClassPayload, { dispatch }) => {
    try {
      dispatch(updateIsCreatingClass(true));
      const { status, data } = await focusBearApi.post(
        Endpoints.CLASS_MANAGEMENT.CLASSES,
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('class_mode.class_created'));
        dispatch(fetchTeacherClasses());
        return data;
      }
      toast.error(t('could_not_process_the_request'));
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === HTTP_STATS_CODE.CONFLICT) {
        toast.error(t('class_mode.class_name_already_exists'));
      } else {
        toastApiRequestError(error);
      }
      captureException(error);
    } finally {
      dispatch(updateIsCreatingClass(false));
    }
  }
);

/**
 * Rename a class (a class is a team, but auth is class-scoped — parent-school
 * admins may rename too, so this uses the class-management route, not the
 * team-management rename which only accepts direct team admins).
 */
export const renameClass = createAsyncThunk(
  'class_management/rename_class',
  async ({ teamId, name }: RenameClassPayload, { dispatch, getState }) => {
    try {
      dispatch(updateIsRenamingClass(true));
      const { status } = await focusBearApi.patch(
        Endpoints.CLASS_MANAGEMENT.CLASS_BY_ID(teamId),
        { name }
      );
      if ([HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)) {
        toast.success(t('class_mode.class_renamed'));
        const { selectedClass } = (getState() as RootState).classManagement;
        if (selectedClass?.id === teamId) {
          dispatch(updateSelectedClass({ ...selectedClass, name }));
        }
        // Keep the overview list (school team page) in sync with the new name.
        dispatch(fetchTeacherClasses());
        return true;
      }
      toast.error(t('could_not_process_the_request'));
      return false;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === HTTP_STATS_CODE.CONFLICT) {
        toast.error(t('class_mode.class_name_already_exists'));
      } else {
        toastApiRequestError(error);
      }
      captureException(error);
      return false;
    } finally {
      dispatch(updateIsRenamingClass(false));
    }
  }
);

/**
 * Soft-archive ("delete" in the UI) a class — reversible, session history is
 * retained and no membership/entitlement is touched. Refused by the backend
 * (409) while a session is live so students aren't stranded mid-session.
 */
export const archiveClass = createAsyncThunk(
  'class_management/archive_class',
  async (teamId: string, { dispatch }) => {
    try {
      dispatch(updateIsArchivingClass(true));
      const { status } = await focusBearApi.post(
        Endpoints.CLASS_MANAGEMENT.CLASS_ARCHIVE(teamId)
      );
      if (
        [
          HTTP_STATS_CODE.SUCCESS,
          HTTP_STATS_CODE.CREATED,
          HTTP_STATS_CODE.NO_CONTENT
        ].includes(status)
      ) {
        toast.success(t('class_mode.class_archived'));
        dispatch(fetchTeacherClasses());
        return true;
      }
      toast.error(t('could_not_process_the_request'));
      return false;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === HTTP_STATS_CODE.CONFLICT) {
        toast.error(t('class_mode.cannot_archive_live'));
      } else {
        toastApiRequestError(error);
      }
      captureException(error);
      return false;
    } finally {
      dispatch(updateIsArchivingClass(false));
    }
  }
);

/** Nullable — powers dashboard reload / live-session discovery for a class. */
export const fetchLiveSession = createAsyncThunk(
  'class_management/fetch_live_session',
  async (teamId: string, { dispatch }) => {
    try {
      dispatch(updateIsLiveSessionLoading(true));
      const { data } = await focusBearApi.get(
        Endpoints.CLASS_MANAGEMENT.LIVE_SESSION(teamId)
      );
      const liveSession: ClassFocusSession | null = data?.id ? data : null;
      dispatch(updateLiveSession(liveSession));
      if (liveSession) {
        dispatch(fetchLiveParticipants(liveSession.id));
        dispatch(fetchUnlockRequests({ sessionId: liveSession.id }));
      } else {
        dispatch(fetchClassSessions({ teamId, adoptMostRecent: true }));
      }
      return liveSession;
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(updateIsLiveSessionLoading(false));
    }
  }
);

/**
 * Class sessions list (`GET .../sessions?team_id=&status=`).
 * `status` filters the list only — live board discovery still uses
 * `GET .../classes/{teamId}/live-session`, never this query alone.
 * With `adoptMostRecent` (ended only), the session with the latest `ended_at`
 * becomes `liveSession` so the ended-session header + save-to-template banner
 * keep their pre-history behaviour after a reload.
 */
export const fetchClassSessions = createAsyncThunk(
  'class_management/fetch_class_sessions',
  async (
    {
      teamId,
      status = CLASS_SESSION_STATUS.ENDED,
      adoptMostRecent = false
    }: {
      teamId: string;
      status?: CLASS_SESSION_STATUS;
      adoptMostRecent?: boolean;
    },
    { dispatch }
  ) => {
    try {
      dispatch(updateAreSessionsLoading(true));
      const { data } = await focusBearApi.get(
        Endpoints.CLASS_MANAGEMENT.SESSIONS,
        {
          params: {
            team_id: teamId,
            status
          }
        }
      );
      const sessions = Array.isArray(data)
        ? (data as ClassSessionListItem[])
        : [];
      dispatch(updateSessions(sessions));
      if (adoptMostRecent && status === CLASS_SESSION_STATUS.ENDED) {
        const recentSession = getMaxBy(
          sessions.filter(
            (session): session is ClassSessionListItem & { ended_at: string } =>
              Boolean(session.ended_at)
          ),
          (session) => new Date(session.ended_at).getTime()
        );
        recentSession?.id && dispatch(updateLiveSession(recentSession));
      }
      return sessions;
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(updateAreSessionsLoading(false));
    }
  }
);

export const startClassSession = createAsyncThunk(
  'class_management/start_class_session',
  async (payload: StartClassSessionPayload, { dispatch }) => {
    try {
      dispatch(updateIsStartingSession(true));
      const { status, data } = await focusBearApi.post(
        Endpoints.CLASS_MANAGEMENT.SESSIONS,
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED && data?.id) {
        dispatch(updateLiveSession(data));
        dispatch(updateShowStartSessionModal(false));
        dispatch(fetchLiveParticipants(data.id));
        // Keep overview chips (live_session, counts) in sync with GET /classes.
        dispatch(fetchTeacherClasses());
        toast.success(t('class_mode.session_started'));
        return data as ClassFocusSession;
      }
      toast.error(t('could_not_process_the_request'));
    } catch (error) {
      const axiosError = error as AxiosError<string | { message?: string }>;
      if (axiosError.response?.status === HTTP_STATS_CODE.CONFLICT) {
        toast.error(t('class_mode.session_already_live'));
        payload.team_id && dispatch(fetchLiveSession(payload.team_id));
      } else {
        toastApiRequestError(error);
      }
      captureException(error);
    } finally {
      dispatch(updateIsStartingSession(false));
    }
  }
);

export const amendAllowlist = createAsyncThunk(
  'class_management/amend_allowlist',
  async (payload: AmendAllowlistPayload, { dispatch, getState }) => {
    const { session_id, ...body } = payload;
    try {
      dispatch(updateIsAmendingAllowlist(true));
      const { status, data } = await focusBearApi.patch(
        Endpoints.CLASS_MANAGEMENT.SESSION_ALLOWLIST(session_id),
        body
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        data?.id && dispatch(updateLiveSession(data));
        toast.success(t('class_mode.allowlist_updated'));
        return data as ClassFocusSession;
      }
      toast.error(t('could_not_process_the_request'));
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === HTTP_STATS_CODE.CONFLICT) {
        // Stale allowlist_version: another teacher tab amended the allow-list
        // first. Reload the latest session state instead of clobbering it.
        toast.info(t('class_mode.allowlist_reloaded'));
        const teamId = (getState() as RootState).classManagement.liveSession
          ?.team_id;
        teamId && dispatch(fetchLiveSession(teamId));
      } else {
        toastApiRequestError(error);
      }
      captureException(error);
    } finally {
      dispatch(updateIsAmendingAllowlist(false));
    }
  }
);

export const amendSessionTasks = createAsyncThunk(
  'class_management/amend_session_tasks',
  async (
    payload: AmendSessionTasksPayload,
    { dispatch, getState, rejectWithValue }
  ) => {
    const { session_id, ...body } = payload;
    try {
      dispatch(updateIsAmendingSessionTasks(true));
      const { status, data } = await focusBearApi.patch(
        Endpoints.CLASS_MANAGEMENT.SESSION_TASKS(session_id),
        body
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        data?.id && dispatch(updateLiveSession(data));
        toast.success(t('class_mode.session_tasks_updated'));
        return data as ClassFocusSession;
      }
      toast.error(t('could_not_process_the_request'));
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === HTTP_STATS_CODE.CONFLICT) {
        const teamId = (getState() as RootState).classManagement.liveSession
          ?.team_id;
        if (teamId) {
          await dispatch(fetchLiveSession(teamId));
        }
        return rejectWithValue(SESSION_TASKS_AMEND_ERROR.VERSION_CONFLICT);
      }
      toastApiRequestError(error);
      captureException(error);
    } finally {
      dispatch(updateIsAmendingSessionTasks(false));
    }
  }
);

export const endClassSession = createAsyncThunk(
  'class_management/end_class_session',
  async (sessionId: string, { dispatch, getState }) => {
    try {
      dispatch(updateIsEndingSession(true));
      const { status, data } = await focusBearApi.post(
        Endpoints.CLASS_MANAGEMENT.SESSION_END(sessionId)
      );
      if ([HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)) {
        const liveSession = (getState() as RootState).classManagement
          .liveSession;
        data?.id
          ? dispatch(updateLiveSession(data))
          : liveSession && dispatch(fetchLiveSession(liveSession.team_id));
        dispatch(fetchSessionReport(sessionId));
        // Keep overview chips (live_session cleared, ended_sessions_count) in sync.
        // Sessions list loads when history modal opens — not on every end.
        dispatch(fetchTeacherClasses());
        toast.success(t('class_mode.session_ended'));
        return data;
      }
      toast.error(t('could_not_process_the_request'));
    } catch (error) {
      toastApiRequestError(error);
      captureException(error);
    } finally {
      dispatch(updateIsEndingSession(false));
    }
  }
);

export const fetchLiveParticipants = createAsyncThunk(
  'class_management/fetch_live_participants',
  async (sessionId: string, { dispatch }) => {
    try {
      dispatch(updateAreParticipantsLoading(true));
      const { data } = await focusBearApi.get(
        Endpoints.CLASS_MANAGEMENT.SESSION_PARTICIPANTS(sessionId)
      );
      // The endpoint returns `{ ...session, participants: [...] }` (same envelope
      // as the report), so the roster lives under `.participants` — not the root.
      dispatch(updateParticipants(data?.participants ?? []));
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(updateAreParticipantsLoading(false));
    }
  }
);

/** Report is stored keyed by session id so expanded history rows coexist. */
export const fetchSessionReport = createAsyncThunk(
  'class_management/fetch_session_report',
  async (sessionId: string, { dispatch }) => {
    try {
      dispatch(updateReportLoadingForSession({ sessionId, isLoading: true }));
      const { data } = await focusBearApi.get(
        Endpoints.CLASS_MANAGEMENT.SESSION_REPORT(sessionId)
      );
      dispatch(updateReportForSession({ sessionId, report: data ?? null }));
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(updateReportLoadingForSession({ sessionId, isLoading: false }));
    }
  }
);

export const fetchUnlockRequests = createAsyncThunk(
  'class_management/fetch_unlock_requests',
  async (
    {
      sessionId,
      status
    }: { sessionId: string; status?: UNLOCK_REQUEST_STATUS },
    { dispatch }
  ) => {
    try {
      dispatch(updateAreUnlockRequestsLoading(true));
      const { data } = await focusBearApi.get(
        Endpoints.CLASS_MANAGEMENT.SESSION_UNLOCK_REQUESTS(sessionId),
        { params: status ? { status } : undefined }
      );
      dispatch(updateUnlockRequests(data ?? []));
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(updateAreUnlockRequestsLoading(false));
    }
  }
);

export const resolveUnlockRequest = createAsyncThunk(
  'class_management/resolve_unlock_request',
  async (
    { request_id, session_id, approve }: ResolveUnlockRequestPayload,
    { dispatch, getState }
  ) => {
    try {
      dispatch(updateResolvingUnlockRequestId(request_id));
      const { status } = await focusBearApi.post(
        approve
          ? Endpoints.CLASS_MANAGEMENT.APPROVE_UNLOCK_REQUEST(request_id)
          : Endpoints.CLASS_MANAGEMENT.DENY_UNLOCK_REQUEST(request_id)
      );
      if ([HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)) {
        toast.success(
          approve
            ? t('class_mode.unlock_request_approved')
            : t('class_mode.unlock_request_denied')
        );
        // Approval appends the app/site to the session allow-list; refresh
        // the session so the editor reflects the new allowlist_version
        // (fetchLiveSession also refetches the unlock requests).
        const teamId = (getState() as RootState).classManagement.liveSession
          ?.team_id;
        if (approve && teamId) {
          dispatch(fetchLiveSession(teamId));
        } else {
          dispatch(fetchUnlockRequests({ sessionId: session_id }));
        }
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      toastApiRequestError(error);
      captureException(error);
    } finally {
      dispatch(updateResolvingUnlockRequestId(null));
    }
  }
);

export const releaseStudent = createAsyncThunk(
  'class_management/release_student',
  async ({ session_id, student_id }: ReleaseStudentPayload, { dispatch }) => {
    try {
      dispatch(updateReleasingStudentId(student_id));
      const { status } = await focusBearApi.post(
        Endpoints.CLASS_MANAGEMENT.RELEASE_STUDENT(session_id),
        { student_id }
      );
      if (
        [
          HTTP_STATS_CODE.SUCCESS,
          HTTP_STATS_CODE.CREATED,
          HTTP_STATS_CODE.NO_CONTENT
        ].includes(status)
      ) {
        toast.success(t('class_mode.student_finished_early'));
        // Roster row flips to LEFT via the server's class-participant-updated
        // event; refetch as a fallback in case the Pusher event is missed.
        dispatch(fetchLiveParticipants(session_id));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(error);
    } finally {
      dispatch(updateReleasingStudentId(null));
    }
  }
);

/**
 * Bulk-resolve every pending unlock request from one student — the teacher's
 * answer to a student spamming requests. One request to the backend, which
 * unions an approval into a single allow-list amend.
 */
export const resolveUnlockRequestsForStudent = createAsyncThunk(
  'class_management/resolve_unlock_requests_for_student',
  async (
    {
      session_id,
      student_id,
      approve
    }: { session_id: string; student_id: string; approve: boolean },
    { dispatch, getState }
  ) => {
    try {
      dispatch(updateResolvingUnlockStudentId(student_id));
      const { status } = await focusBearApi.post(
        approve
          ? Endpoints.CLASS_MANAGEMENT.APPROVE_ALL_UNLOCK_REQUESTS(session_id)
          : Endpoints.CLASS_MANAGEMENT.REJECT_ALL_UNLOCK_REQUESTS(session_id),
        { student_id }
      );
      if ([HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)) {
        toast.success(
          approve
            ? t('class_mode.unlock_requests_approved_all')
            : t('class_mode.unlock_requests_rejected_all')
        );
        const teamId = (getState() as RootState).classManagement.liveSession
          ?.team_id;
        if (approve && teamId) {
          dispatch(fetchLiveSession(teamId));
        } else {
          dispatch(fetchUnlockRequests({ sessionId: session_id }));
        }
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      toastApiRequestError(error);
      captureException(error);
    } finally {
      dispatch(updateResolvingUnlockStudentId(null));
    }
  }
);

/**
 * Class roster — a class is a team, so this reuses the team-management
 * all-members endpoint (kept class-scoped so it never clobbers the team
 * page's own member state).
 */
export const fetchClassMembers = createAsyncThunk(
  'class_management/fetch_class_members',
  async (classId: string, { dispatch }) => {
    try {
      dispatch(updateAreClassMembersLoading(true));
      const { data } = await focusBearApi.get(
        `${Endpoints.TEAM_MANAGEMENT}/all-members`,
        { params: { team_id: classId } }
      );
      const rosterPayload = data as TeamsMembers | undefined;
      const roster = dedupeClassRoster({
        members: rosterPayload?.members ?? [],
        admins: rosterPayload?.admins ?? [],
        teachers: rosterPayload?.teachers ?? []
      });
      dispatch(
        updateClassMembers({
          ...roster,
          ...buildClassRosterPartitions(roster)
        })
      );
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(updateAreClassMembersLoading(false));
    }
  }
);

/**
 * Parent-team members offered for enrolment into the class (plan F2).
 * Teachers (restricted access) must pass `q` (min 2 chars); admins may omit
 * `q` to load the browsable roster. Pass `shouldUpdateList: false` for
 * admin local-miss → `?q=` fallback without wiping the Redux list.
 */
export const fetchCandidateMembers = createAsyncThunk(
  'class_management/fetch_candidate_members',
  async (
    {
      class_id,
      query,
      shouldUpdateList = true
    }: FetchCandidateMembersArgs,
    { dispatch }
  ): Promise<ClassCandidateMember[]> => {
    const fetchId = shouldUpdateList
      ? beginCandidateListFetch()
      : beginCandidateSearchFetch();
    const isCurrentFetch = () =>
      shouldUpdateList
        ? isCurrentCandidateListFetch(fetchId)
        : isCurrentCandidateSearchFetch(fetchId);

    try {
      if (shouldUpdateList) {
        dispatch(updateAreCandidateMembersLoading(true));
      }
      const { data } = await focusBearApi.get(
        Endpoints.CLASS_MANAGEMENT.CANDIDATE_MEMBERS(class_id),
        { params: query ? { q: query } : undefined }
      );
      if (!isCurrentFetch()) {
        return [];
      }
      const candidates = data?.candidates as ClassCandidateMember[] | undefined;
      const nextCandidates = candidates ?? [];
      if (shouldUpdateList) {
        dispatch(updateCandidateMembers(nextCandidates));
        dispatch(
          updateAreCandidateMembersRestricted(data?.is_restricted === true)
        );
      }
      return nextCandidates;
    } catch (error) {
      captureException(error);
      return [];
    } finally {
      if (shouldUpdateList && isCurrentFetch()) {
        dispatch(updateAreCandidateMembersLoading(false));
      }
    }
  }
);

/**
 * Clears restricted search hits and invalidates in-flight list fetches so a late
 * `?q=` response cannot refill the list after the query drops below min length.
 */
export const clearRestrictedCandidateMembers = createAsyncThunk(
  'class_management/clear_restricted_candidate_members',
  async (_, { dispatch }) => {
    invalidateCandidateMemberFetches();
    dispatch(updateCandidateMembers([]));
    dispatch(updateAreCandidateMembersLoading(false));
  }
);

/** Batch-add parent-team members to the class as students or teachers. */
export const addClassMembers = createAsyncThunk(
  'class_management/add_class_members',
  async ({ class_id, members }: AddClassMembersPayload, { dispatch }) => {
    try {
      dispatch(updateIsAddingMembers(true));
      const { status } = await focusBearApi.post(
        Endpoints.CLASS_MANAGEMENT.CLASS_MEMBERS(class_id),
        { members }
      );
      if ([HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)) {
        toast.success(t('class_mode.members_added'));
        dispatch(fetchClassMembers(class_id));
        dispatch(fetchCandidateMembers({ class_id }));
        return true;
      }
      toast.error(t('could_not_process_the_request'));
      return false;
    } catch (error) {
      toastApiRequestError(error);
      captureException(error);
      return false;
    } finally {
      dispatch(updateIsAddingMembers(false));
    }
  }
);

/** Removes a member from the class only — never from the parent team. */
export const removeClassMember = createAsyncThunk(
  'class_management/remove_class_member',
  async ({ class_id, member_id }: RemoveClassMemberPayload, { dispatch }) => {
    try {
      dispatch(updateRemovingMemberId(member_id));
      const { status } = await focusBearApi.delete(
        Endpoints.CLASS_MANAGEMENT.CLASS_MEMBER_BY_ID(class_id, member_id)
      );
      if ([HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)) {
        toast.success(t('class_mode.member_removed'));
        dispatch(fetchClassMembers(class_id));
        dispatch(fetchCandidateMembers({ class_id }));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      // Surface the backend's reason (e.g. "the class owner cannot be removed") —
      // a generic toast made real rejections indistinguishable from outages.
      toastApiRequestError(error);
      captureException(error);
    } finally {
      dispatch(updateRemovingMemberId(null));
    }
  }
);

/** Per-student attendance matrix across all ended sessions. */
export const fetchClassAttendance = createAsyncThunk(
  'class_management/fetch_class_attendance',
  async (classId: string, { dispatch }) => {
    try {
      dispatch(updateAreAttendanceLoading(true));
      const { data } = await focusBearApi.get(
        Endpoints.CLASS_MANAGEMENT.CLASS_ATTENDANCE(classId)
      );
      const attendance = data as ClassAttendanceResponse | undefined;
      dispatch(updateAttendance(attendance?.students ?? []));
      dispatch(setConcernCriteria(attendance?.concern_criteria ?? null));
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(updateAreAttendanceLoading(false));
    }
  }
);

export const updateConcernCriteria = createAsyncThunk(
  'class_management/update_concern_criteria',
  async (
    { classId, criteria }: UpdateConcernCriteriaPayload,
    { dispatch }
  ) => {
    try {
      const { data } = await focusBearApi.patch(
        Endpoints.CLASS_MANAGEMENT.CLASS_CONCERN_CRITERIA(classId),
        criteria
      );
      const saved = (data as ClassConcernCriteria | undefined) ?? criteria;
      dispatch(setConcernCriteria(saved));
      toast.success(t('class_mode.concern_criteria_saved'));
      return saved;
    } catch (error) {
      captureException(error);
      toastApiRequestError(error);
      return null;
    }
  }
);
